import { ReactNode, useEffect, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { FilterDesktop } from 'components/filter/FilterDesktop';
import { FilterMobile } from 'components/filter/FilterMobile';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useFieldKeyPress } from 'hooks/useFieldKeyPress';
import { useFilter, ValueSetterFiltersProps } from 'hooks/useFilter';

export type FilterProps = {
    children?: ReactNode;
    showTitle?: boolean;
    closeOnSubmit?: boolean;
    fixed?: boolean;
    preventOnEnter?: boolean;
    loadingSubmit?: boolean;
    filters?: ValueSetterFiltersProps[];
    preventQueryParamsEmptyStart?: boolean;

    init?(): void;
    reset?(): void;
    submit?(): void;
};

export function Filter({
    children,
    filters,
    preventQueryParamsEmptyStart = false,
    init,
    reset,
    submit,
    fixed = true,
    closeOnSubmit = true,
    preventOnEnter = false,
    loadingSubmit = false,
}: FilterProps) {
    const { isDownLg: isMobile } = useBreakpoints();
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const pressEnterInsideField = useFieldKeyPress('Enter');
    const { handleFilterSubmit, handleFilterReset, setDefaultFilters } = useFilter();

    /**
     * Inicializa o filtro:
     * 1. Executa a função init se ela existir
     * 2. Caso contrário, executa setDefaultFilters
     */
    useEffect(() => {
        if (init) init();
        else if (filters) setDefaultFilters({ filters, preventQueryParamsEmptyStart });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Realiza a submissão do filtro:
     * 1. Executa a função submit se ela existir
     * 2. Caso contrário, executa handleFilterSubmit
     */
    const handleSubmit = () => {
        if (submit) submit();
        else if (filters) handleFilterSubmit({ filters });
    };

    /**
     * Realiza o reset do filtro:
     * 1. Executa a função reset se ela existir
     * 2. Caso contrário, executa handleFilterReset
     */
    const handleReset = () => {
        if (reset) reset();
        else if (filters) handleFilterReset({ filters });
    };

    /**
     * Submete o filtro e fecha o drawer se estiver no mobile.
     */
    const onSubmit = () => {
        handleSubmit();

        if (isMobile && closeOnSubmit) {
            setOpenDrawer(false);
        }
    };

    /**
     * Adia a execução da submissão do filtro em 250ms.
     */
    const debouncedSubmit = useDebouncedCallback(() => onSubmit(), 250);

    if (pressEnterInsideField && !preventOnEnter) debouncedSubmit();

    if (isMobile)
        return (
            <FilterMobile
                open={openDrawer}
                loadingSubmit={loadingSubmit}
                onOpen={() => setOpenDrawer(true)}
                onClose={() => setOpenDrawer(false)}
                reset={handleReset}
                submit={onSubmit}
            >
                {children}
            </FilterMobile>
        );

    return (
        <FilterDesktop fixed={fixed} loadingSubmit={loadingSubmit} reset={handleReset} submit={onSubmit}>
            {children}
        </FilterDesktop>
    );
}
